import { getApiBaseUrl } from "../config/apiBase";
import { apiFetch } from "../config/apiFetch";

async function parseError(res: Response): Promise<string> {
  const text = await res.text();
  try {
    const j = JSON.parse(text) as { error?: string; message?: string; details?: string };
    const parts: string[] = [];
    if (j.error) parts.push(j.error);
    if (j.message && j.message !== j.error) parts.push(j.message);
    if (j.details) parts.push(j.details);
    if (parts.length) return parts.join("\n\n");
  } catch {
    /* ignore */
  }
  return text || `Request failed (${res.status})`;
}

export type MrrPoStatus = "Completed" | "Partial" | "Not Yet Raised";

export interface MrrPoCompletedRow {
  id: number;
  mrrNumber: string;
  projectId: string;
  projectName: string | null;
  date: string | null;
  poMode: string;
  poNumber: string | null;
  poDate: string | null;
  status: string;
  approvedAt: string | null;
  poStatus: MrrPoStatus;
  lineCount: number;
}

export interface MrrLineItem {
  id: number;
  sno: number;
  itemName: string | null;
  make: string | null;
  unit: string | null;
  hsnCode: string | null;
  poQty: number | null;
  poNumber: string | null;
  poDate: string | null;
  poAttachmentPath: string | null;
  qtyToOrder: number | null;
  currentRequiredQty: number | null;
  itemStatus: string | null;
}

export interface MrrDetail extends Omit<MrrPoCompletedRow, "lineCount"> {
  requestType: string | null;
  requisitionType: string | null;
  description: string | null;
  deliveryDetails: string | null;
  createdBy: string;
  poAttachmentPath: string | null;
  poStatus: MrrPoStatus;
  items: MrrLineItem[];
}

export interface MrrGoodsReceiptLine {
  itemId: number;
  qtyReceived: number;
  /** Quantity failing quality check (optional on legacy rows; defaults to 0). */
  qtyRejected?: number;
  /** From MRR `mrr_material_request_items` when API can reach MRR DB. */
  itemName?: string | null;
  itemUnit?: string | null;
}

export interface MrrGoodsReceipt {
  id: string;
  mrrId: number;
  mrrNumber: string;
  storeYardId: string;
  storeYardName: string;
  receivedBy: string;
  notes: string;
  lineReceipts: MrrGoodsReceiptLine[];
  createdAt: string;
}

export async function fetchMrrPoCompletedList(limit = 250): Promise<MrrPoCompletedRow[]> {
  const res = await apiFetch(
    `${getApiBaseUrl()}/api/inventory/mrr/po-completed?limit=${encodeURIComponent(String(limit))}`,
  );
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<MrrPoCompletedRow[]>;
}

export async function fetchMrrDetail(mrrId: number): Promise<MrrDetail> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/inventory/mrr/${encodeURIComponent(String(mrrId))}`);
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<MrrDetail>;
}

export async function postMrrGoodsReceipt(
  mrrId: number,
  body: {
    storeYardId: string;
    receivedBy?: string;
    notes?: string;
    lines: MrrGoodsReceiptLine[];
  },
): Promise<MrrGoodsReceipt> {
  const res = await apiFetch(
    `${getApiBaseUrl()}/api/inventory/mrr/${encodeURIComponent(String(mrrId))}/goods-receipt`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<MrrGoodsReceipt>;
}

export interface GoodsReceiptsListQuery {
  /** Max rows returned (server caps at 2000). */
  limit?: number;
  storeYardId?: string;
  /** Matches receipt #, MRR #, store name, received by, MRR id, notes. */
  search?: string;
  /** Inclusive, YYYY-MM-DD. */
  from?: string;
  /** Inclusive, YYYY-MM-DD. */
  to?: string;
  sort?: "asc" | "desc";
  /** Filter by material request id in the operational table. */
  mrrId?: number;
}

export async function fetchMrrGoodsReceipts(query: GoodsReceiptsListQuery | number = {}): Promise<MrrGoodsReceipt[]> {
  const q: GoodsReceiptsListQuery = typeof query === "number" ? { limit: query } : query;
  const params = new URLSearchParams();
  const lim = Math.min(2000, Math.max(1, q.limit ?? 1500));
  params.set("limit", String(lim));
  if (q.storeYardId?.trim()) params.set("storeYardId", q.storeYardId.trim());
  if (q.search?.trim()) params.set("search", q.search.trim());
  if (q.from?.trim()) params.set("from", q.from.trim());
  if (q.to?.trim()) params.set("to", q.to.trim());
  if (q.sort === "asc" || q.sort === "desc") params.set("sort", q.sort);
  if (typeof q.mrrId === "number" && Number.isFinite(q.mrrId) && q.mrrId > 0) {
    params.set("mrrId", String(Math.floor(q.mrrId)));
  }
  const res = await apiFetch(`${getApiBaseUrl()}/api/inventory/mrr-goods-receipts?${params.toString()}`);
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<MrrGoodsReceipt[]>;
}

export async function fetchGoodsReceiptById(receiptId: string): Promise<MrrGoodsReceipt> {
  const res = await apiFetch(
    `${getApiBaseUrl()}/api/inventory/mrr-goods-receipts/${encodeURIComponent(receiptId)}`,
  );
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<MrrGoodsReceipt>;
}

export interface StoreStockRow {
  storeYardId: string;
  storeYardName: string;
  catalogStandardItemId: string;
  resourceItemLabel: string;
  resourceItemUnit: string;
  totalInward: number;
  totalOutward: number;
  currentStock: number;
}

export async function fetchStoreStocks(storeYardId?: string): Promise<StoreStockRow[]> {
  const q =
    storeYardId && storeYardId.trim()
      ? `?storeYardId=${encodeURIComponent(storeYardId.trim())}`
      : "";
  const res = await apiFetch(`${getApiBaseUrl()}/api/inventory/store-stocks${q}`);
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<StoreStockRow[]>;
}

export interface StoreAuditWorksheetDto {
  physicalByRow: Record<string, string>;
  remarkByRow: Record<string, string>;
  savedAt: string | null;
}

export async function fetchStoreAuditWorksheet(storeYardId?: string): Promise<StoreAuditWorksheetDto> {
  const q =
    storeYardId && storeYardId.trim()
      ? `?storeYardId=${encodeURIComponent(storeYardId.trim())}`
      : "";
  const res = await apiFetch(`${getApiBaseUrl()}/api/inventory/store-audit-worksheet${q}`);
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<StoreAuditWorksheetDto>;
}

export async function saveStoreAuditWorksheet(payload: {
  storeYardId?: string;
  physicalByRow: Record<string, string>;
  remarkByRow: Record<string, string>;
}): Promise<{ ok: boolean; savedAt: string }> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/inventory/store-audit-worksheet`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      storeYardId: payload.storeYardId?.trim() || "",
      physicalByRow: payload.physicalByRow,
      remarkByRow: payload.remarkByRow,
    }),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<{ ok: boolean; savedAt: string }>;
}

export interface StoreOutwardRow {
  id: string;
  storeYardId: string;
  storeYardName: string;
  catalogStandardItemId: string | null;
  resourceItemLabel: string;
  resourceItemUnit: string;
  qty: number;
  subcontractorMasterId: string;
  subcontractorFirmName: string;
  applicationId: string;
  beneficiaryName: string | null;
  beneficiaryMobileApplicationId: string | null;
  notes: string;
  createdAt: string;
}

export interface StoreOutwardListQuery {
  limit?: number;
  storeYardId?: string;
  subcontractorMasterId?: string;
  search?: string;
  sort?: "asc" | "desc";
}

export async function fetchStoreOutwardRegister(q: StoreOutwardListQuery = {}): Promise<StoreOutwardRow[]> {
  const params = new URLSearchParams();
  const lim = Math.min(2000, Math.max(1, q.limit ?? 800));
  params.set("limit", String(lim));
  if (q.storeYardId?.trim()) params.set("storeYardId", q.storeYardId.trim());
  if (q.subcontractorMasterId?.trim()) params.set("subcontractorMasterId", q.subcontractorMasterId.trim());
  if (q.search?.trim()) params.set("search", q.search.trim());
  if (q.sort === "asc" || q.sort === "desc") params.set("sort", q.sort);
  const res = await apiFetch(`${getApiBaseUrl()}/api/inventory/store-outward?${params.toString()}`);
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<StoreOutwardRow[]>;
}

export interface StoreOutwardAnalyticsTotals {
  totalDispatches: number;
  totalQty: number;
  uniqueBeneficiaries: number;
  distinctItems: number;
}

export interface StoreOutwardAnalyticsRecent {
  today: number;
  last7Days: number;
  last30Days: number;
}

export interface StoreOutwardAnalyticsByStore {
  storeYardId: string;
  storeYardName: string;
  dispatchCount: number;
  totalQty: number;
  uniqueBeneficiaries: number;
}

export interface StoreOutwardAnalyticsBySubcontractor {
  subcontractorMasterId: string;
  subcontractorFirmName: string;
  dispatchCount: number;
  totalQty: number;
  uniqueBeneficiaries: number;
}

export interface StoreOutwardAnalyticsTopItem {
  catalogStandardItemId: string | null;
  resourceItemLabel: string;
  resourceItemUnit: string;
  dispatchCount: number;
  totalQty: number;
}

export interface StoreOutwardAnalytics {
  totals: StoreOutwardAnalyticsTotals;
  recent: StoreOutwardAnalyticsRecent;
  byStoreYard: StoreOutwardAnalyticsByStore[];
  bySubcontractor: StoreOutwardAnalyticsBySubcontractor[];
  topItems: StoreOutwardAnalyticsTopItem[];
}

export async function fetchStoreOutwardAnalytics(): Promise<StoreOutwardAnalytics> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/inventory/store-outward/analytics`);
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<StoreOutwardAnalytics>;
}

export type IssueMaterialStatus = "pending" | "partial" | "completed";

export interface IssueMaterialRow {
  id: string;
  generatedBatchId: string;
  applicationId: string;
  beneficiaryName: string | null;
  beneficiaryMobileApplicationId: string | null;
  subcontractorMasterId: string;
  subcontractorFirmName: string;
  milestoneTemplateId: string;
  milestoneTitle: string;
  standardResourceId: string;
  standardResourceTitle: string;
  standardResourceItemId: string;
  catalogStandardItemId: string;
  resourceItemLabel: string;
  resourceItemUnit: string;
  resourceItemRate: number;
  notes: string;
  plannedQty: number;
  issuedQty: number;
  balanceQty: number;
  status: IssueMaterialStatus;
  sourcePoId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface IssueMaterialListQuery {
  limit?: number;
  applicationId?: string;
  milestoneTemplateId?: string;
  subcontractorMasterId?: string;
  status?: IssueMaterialStatus;
  search?: string;
  sort?: "asc" | "desc";
}

export async function fetchIssueMaterialRegister(q: IssueMaterialListQuery = {}): Promise<IssueMaterialRow[]> {
  const params = new URLSearchParams();
  const lim = Math.min(2000, Math.max(1, q.limit ?? 800));
  params.set("limit", String(lim));
  if (q.applicationId?.trim()) params.set("applicationId", q.applicationId.trim());
  if (q.milestoneTemplateId?.trim()) params.set("milestoneTemplateId", q.milestoneTemplateId.trim());
  if (q.subcontractorMasterId?.trim()) params.set("subcontractorMasterId", q.subcontractorMasterId.trim());
  if (q.status === "pending" || q.status === "partial" || q.status === "completed") params.set("status", q.status);
  if (q.search?.trim()) params.set("search", q.search.trim());
  if (q.sort === "asc" || q.sort === "desc") params.set("sort", q.sort);
  const res = await apiFetch(`${getApiBaseUrl()}/api/inventory/issue-material?${params.toString()}`);
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<IssueMaterialRow[]>;
}

export async function fetchIssueMaterialPendingOutward(q: {
  limit?: number;
  applicationId?: string;
  subcontractorMasterId?: string;
} = {}): Promise<IssueMaterialRow[]> {
  const params = new URLSearchParams();
  const lim = Math.min(3000, Math.max(1, q.limit ?? 1500));
  params.set("limit", String(lim));
  if (q.applicationId?.trim()) params.set("applicationId", q.applicationId.trim());
  if (q.subcontractorMasterId?.trim()) params.set("subcontractorMasterId", q.subcontractorMasterId.trim());
  const res = await apiFetch(`${getApiBaseUrl()}/api/inventory/issue-material/pending-outward?${params.toString()}`);
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<IssueMaterialRow[]>;
}

export async function generateIssueMaterialRows(body: {
  applicationId: string;
  milestoneTemplateId: string;
  mode: "all" | "selected";
  selectedResourceIds?: string[];
  notes?: string;
}): Promise<{
  created: number;
  skipped: number;
  applicationId: string;
  milestoneTemplateId: string;
  rows: IssueMaterialRow[];
}> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/inventory/issue-material/generate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<{
    created: number;
    skipped: number;
    applicationId: string;
    milestoneTemplateId: string;
    rows: IssueMaterialRow[];
  }>;
}

/** Create issue material rows from an existing PO (full or partial qty per line). */
export async function generateIssueMaterialFromPo(body: {
  poId: string;
  notes?: string;
  mode?: "full" | "partial";
  quantityOverrides?: Record<string, number>;
}): Promise<{
  created: number;
  generatedBatchId: string;
  beneficiaryCount: number;
  poNumber: string;
  milestoneTemplateId: string;
  mode: "full" | "partial";
}> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/inventory/issue-material/generate-from-po`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<{
    created: number;
    generatedBatchId: string;
    beneficiaryCount: number;
    poNumber: string;
    milestoneTemplateId: string;
    mode: "full" | "partial";
  }>;
}

export async function dispatchIssueMaterial(
  issueId: string,
  body: {
    storeYardId: string;
    storeYardName?: string;
    mode: "full" | "partial";
    qty?: number;
    notes?: string;
  },
): Promise<{
  issueRow: IssueMaterialRow;
  outwardRow: StoreOutwardRow;
  dispatchQty: number;
  storeStockAfterDispatch: number;
}> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/inventory/issue-material/${encodeURIComponent(issueId)}/dispatch`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<{
    issueRow: IssueMaterialRow;
    outwardRow: StoreOutwardRow;
    dispatchQty: number;
    storeStockAfterDispatch: number;
  }>;
}

export type IssueMaterialDispatchOtpPlan = {
  issueId: string;
  mode: "full" | "partial";
  /** Required when mode is partial. */
  qty?: number;
};

export async function sendIssueMaterialDispatchOtp(body: {
  subcontractorMasterId: string;
  storeYardId: string;
  storeYardName?: string;
  plans: IssueMaterialDispatchOtpPlan[];
}): Promise<{ challengeId: string; emailHint: string; expiresInMinutes: number }> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/inventory/issue-material/dispatch-otp/send`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<{ challengeId: string; emailHint: string; expiresInMinutes: number }>;
}

export async function confirmIssueMaterialDispatchOtp(body: {
  challengeId: string;
  otp: string;
}): Promise<{
  ok: true;
  dispatched: number;
  results: unknown[];
}> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/inventory/issue-material/dispatch-otp/confirm`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<{ ok: true; dispatched: number; results: unknown[] }>;
}

export interface IssuePoHeader {
  id: string;
  poNumber: string;
  milestoneTemplateId: string;
  milestoneTitle: string;
  subcontractorMasterId: string;
  subcontractorFirmName: string;
  beneficiaryCount: number;
  selectionMode: "full" | "partial";
  notes: string;
  /** YYYY-MM-DD — calendar date when the PO was generated (from DB `po_date`; stable). */
  poDate?: string | null;
  createdAt: string;
  updatedAt: string;
  /** Saved at PO generation — used on PDF “Ship to” only. */
  shipToPrintText?: string;
  shipToKind?: "store" | "beneficiary" | "";
  shipToStoreYardId?: string | null;
  shipToApplicationId?: string | null;
  /** active | cancelled — cancelled POs are hidden from registers and selection lists */
  status?: "active" | "cancelled";
}

export interface IssuePoItem {
  id: string;
  poId: string;
  applicationId: string;
  beneficiaryName: string | null;
  beneficiaryMobileApplicationId: string | null;
  standardResourceId: string;
  standardResourceTitle: string;
  standardResourceItemId: string;
  catalogStandardItemId: string;
  resourceItemLabel: string;
  resourceItemUnit: string;
  plannedQty: number;
  resourceItemRate: number;
  /** HSN/SAC copied from standard resource line at PO generation. */
  hsnCode: string;
  createdAt: string;
}

export type IssuePoShipToInput =
  | { kind: "store"; storeYardId: string }
  | { kind: "beneficiary"; applicationId: string };

export async function generateIssuePo(body: {
  milestoneTemplateId: string;
  applicationIds: string[];
  selectionMode: "full" | "partial";
  selectedResourceIds?: string[];
  selectedResourceItemIds?: string[];
  quantityOverrides?: Record<string, number>;
  notes?: string;
  shipTo: IssuePoShipToInput;
}): Promise<{ po: IssuePoHeader; items: IssuePoItem[] }> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/inventory/issue-po/generate`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<{ po: IssuePoHeader; items: IssuePoItem[] }>;
}

export async function cancelIssuePo(poId: string): Promise<{ ok: true; removedIssueMaterialRows: number }> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/inventory/issue-po/${encodeURIComponent(poId)}/cancel`, {
    method: "POST",
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<{ ok: true; removedIssueMaterialRows: number }>;
}

/** @deprecated Use cancelIssuePo — kept for compatibility */
export async function deleteIssuePo(poId: string): Promise<{ ok: true; deletedIssueMaterialRows: number }> {
  const result = await cancelIssuePo(poId);
  return { ok: true, deletedIssueMaterialRows: result.removedIssueMaterialRows };
}

export interface IssuePoBlockedMilestoneLine {
  applicationId: string;
  poNumbers: string[];
}

export interface IssuePoBlockedMilestoneGroup {
  milestoneTemplateId: string;
  milestoneTitle: string;
  lines: IssuePoBlockedMilestoneLine[];
}

/** Milestones omitted from PO generation: full PO already on file; includes PO numbers per beneficiary. */
export async function fetchIssuePoBlockedMilestones(applicationIds: string[]): Promise<{
  applicationIds: string[];
  milestoneTemplateIds: string[];
  blockedFullPos: IssuePoBlockedMilestoneGroup[];
}> {
  const ids = [...new Set(applicationIds.map((x) => String(x).trim()).filter(Boolean))];
  const res = await apiFetch(`${getApiBaseUrl()}/api/inventory/issue-po/blocked-milestones`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ applicationIds: ids }),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<{
    applicationIds: string[];
    milestoneTemplateIds: string[];
    blockedFullPos: IssuePoBlockedMilestoneGroup[];
  }>;
}

export interface MasterPlanItem {
  standardResourceItemId: string;
  standardResourceId: string;
  standardResourceTitle: string;
  resourceItemLabel: string;
  resourceItemUnit: string;
  masterPlannedQty: number;
}

/** Min remaining PO qty per standard resource line across selected beneficiaries (master plan − earlier POs). */
export async function fetchIssuePoRemainingQtyByItem(q: {
  milestoneTemplateId: string;
  applicationIds: string[];
}): Promise<{
  milestoneTemplateId: string;
  applicationIds: string[];
  remainingQtyByStandardResourceItemId: Record<string, number>;
  masterPlannedQtyByStandardResourceItemId: Record<string, number>;
  masterItems: MasterPlanItem[];
}> {
  const applicationIds = [...new Set(q.applicationIds.map((x) => String(x).trim()).filter(Boolean))];
  const res = await apiFetch(`${getApiBaseUrl()}/api/inventory/issue-po/remaining-qty-by-item`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      milestoneTemplateId: q.milestoneTemplateId.trim(),
      applicationIds,
    }),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<{
    milestoneTemplateId: string;
    applicationIds: string[];
    remainingQtyByStandardResourceItemId: Record<string, number>;
    masterPlannedQtyByStandardResourceItemId: Record<string, number>;
    masterItems: MasterPlanItem[];
  }>;
}

export async function fetchIssuePoRegister(q: {
  limit?: number;
  subcontractorMasterId?: string;
  milestoneTemplateId?: string;
  search?: string;
  sort?: "asc" | "desc";
} = {}): Promise<IssuePoHeader[]> {
  const params = new URLSearchParams();
  const lim = Math.min(2000, Math.max(1, q.limit ?? 500));
  params.set("limit", String(lim));
  if (q.subcontractorMasterId?.trim()) params.set("subcontractorMasterId", q.subcontractorMasterId.trim());
  if (q.milestoneTemplateId?.trim()) params.set("milestoneTemplateId", q.milestoneTemplateId.trim());
  if (q.search?.trim()) params.set("search", q.search.trim());
  if (q.sort === "asc" || q.sort === "desc") params.set("sort", q.sort);
  const res = await apiFetch(`${getApiBaseUrl()}/api/inventory/issue-po?${params.toString()}`);
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<IssuePoHeader[]>;
}

export interface IssuePoMilestoneStat {
  milestoneTemplateId: string;
  milestoneTitle: string;
  poCount: number;
  fullCount: number;
  partialCount: number;
  uniqueBeneficiaries: number;
  itemCount: number;
  totalPlannedQty: number;
}

export interface IssuePoSubcontractorStat {
  subcontractorMasterId: string;
  subcontractorFirmName: string;
  poCount: number;
  fullCount: number;
  partialCount: number;
  uniqueBeneficiaries: number;
  itemCount: number;
  totalPlannedQty: number;
}

export interface IssuePoAnalytics {
  totals: {
    totalPo: number;
    fullPo: number;
    partialPo: number;
    totalBeneficiaryLines: number;
    uniqueBeneficiaries: number;
    /** Beneficiary applications linked to a subcontractor (same rule as PO generation). */
    totalEligibleBeneficiaries: number;
    /** Distinct beneficiaries appearing on at least one full (complete) PO. */
    beneficiariesWithFullPo: number;
    /** Distinct beneficiaries appearing on at least one partial PO. */
    beneficiariesWithPartialPo: number;
    totalItems: number;
    totalPlannedQty: number;
  };
  recent: { today: number; last7Days: number; last30Days: number };
  byMilestone: IssuePoMilestoneStat[];
  bySubcontractor: IssuePoSubcontractorStat[];
}

export async function fetchIssuePoAnalytics(): Promise<IssuePoAnalytics> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/inventory/issue-po/analytics`);
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<IssuePoAnalytics>;
}

export interface IssueMaterialMilestoneStat {
  milestoneTemplateId: string;
  milestoneTitle: string;
  totalRows: number;
  pendingRows: number;
  partialRows: number;
  completedRows: number;
  uniqueBeneficiaries: number;
  totalPlannedQty: number;
  totalIssuedQty: number;
  totalBalanceQty: number;
}

export interface IssueMaterialSubcontractorStat {
  subcontractorMasterId: string;
  subcontractorFirmName: string;
  totalRows: number;
  pendingRows: number;
  partialRows: number;
  completedRows: number;
  uniqueBeneficiaries: number;
  totalPlannedQty: number;
  totalIssuedQty: number;
  totalBalanceQty: number;
}

export interface IssueMaterialAnalytics {
  totals: {
    totalRows: number;
    pendingRows: number;
    partialRows: number;
    completedRows: number;
    uniqueBeneficiaries: number;
    beneficiariesCompleted: number;
    beneficiariesPartial: number;
    beneficiariesPending: number;
    totalPlannedQty: number;
    totalIssuedQty: number;
    totalBalanceQty: number;
    issueRatePct: number;
  };
  recent: { today: number; last7Days: number; last30Days: number };
  byMilestone: IssueMaterialMilestoneStat[];
  bySubcontractor: IssueMaterialSubcontractorStat[];
}

export async function fetchIssueMaterialAnalytics(): Promise<IssueMaterialAnalytics> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/inventory/issue-material/analytics`);
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<IssueMaterialAnalytics>;
}

export async function fetchIssuePoItems(poId: string): Promise<IssuePoItem[]> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/inventory/issue-po/${encodeURIComponent(poId)}/items`);
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<IssuePoItem[]>;
}

export async function postStoreOutward(body: {
  storeYardId: string;
  storeYardName?: string;
  catalogStandardItemId?: string;
  resourceItemLabel: string;
  resourceItemUnit: string;
  qty: number;
  subcontractorMasterId: string;
  subcontractorFirmName: string;
  applicationId: string;
  beneficiaryName?: string;
  beneficiaryMobileApplicationId?: string;
  notes?: string;
}): Promise<StoreOutwardRow> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/inventory/store-outward`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<StoreOutwardRow>;
}

export interface StoreInwardLine {
  catalogStandardItemId?: string;
  resourceItemLabel: string;
  resourceItemUnit: string;
  qtyReceived: number;
  qtyRejected?: number;
}

export interface StoreInwardReceipt {
  id: string;
  poId: string;
  poNumber: string;
  storeYardId: string;
  storeYardName: string;
  receivedBy: string;
  notes: string;
  lineReceipts: StoreInwardLine[];
  createdAt: string;
}

export interface StoreInwardListQuery {
  limit?: number;
  storeYardId?: string;
  search?: string;
  from?: string;
  to?: string;
  sort?: "asc" | "desc";
}

export async function postStoreInward(body: {
  poId: string;
  poNumber: string;
  storeYardId: string;
  receivedBy?: string;
  notes?: string;
  lines: StoreInwardLine[];
}): Promise<StoreInwardReceipt> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/inventory/store-inward`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<StoreInwardReceipt>;
}

export interface PoReceiptBalanceLine {
  lineKey: string;
  catalogStandardItemId: string;
  resourceItemLabel: string;
  resourceItemUnit: string;
  poQty: number;
  receivedQty: number;
  yetToReceive: number;
}

export async function fetchOpenPoIdsForInward(): Promise<string[]> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/inventory/store-inward/open-po-ids`);
  if (!res.ok) throw new Error(await parseError(res));
  const data = (await res.json()) as { poIds?: string[] };
  return Array.isArray(data.poIds) ? data.poIds : [];
}

export async function fetchPoReceiptBalance(poId: string): Promise<{
  poId: string;
  items: PoReceiptBalanceLine[];
  hasOpenBalance: boolean;
}> {
  const pid = poId.trim();
  const res = await apiFetch(`${getApiBaseUrl()}/api/inventory/store-inward/po-receipt-balance/${encodeURIComponent(pid)}`);
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<{
    poId: string;
    items: PoReceiptBalanceLine[];
    hasOpenBalance: boolean;
  }>;
}

export interface PoIssueBalanceLine {
  poItemId: string;
  applicationId: string;
  standardResourceItemId: string;
  catalogStandardItemId: string;
  resourceItemLabel: string;
  resourceItemUnit: string;
  poQty: number;
  alreadyIssued: number;
  yetToIssue: number;
}

export async function fetchOpenPoIdsForIssue(): Promise<string[]> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/inventory/issue-material/open-po-ids-for-issue`);
  if (!res.ok) throw new Error(await parseError(res));
  const data = (await res.json()) as { poIds?: string[] };
  return Array.isArray(data.poIds) ? data.poIds : [];
}

export async function fetchPoIssueBalance(poId: string): Promise<{
  poId: string;
  poNumber: string;
  items: PoIssueBalanceLine[];
  hasOpenBalance: boolean;
}> {
  const pid = poId.trim();
  const res = await apiFetch(`${getApiBaseUrl()}/api/inventory/issue-material/po-issue-balance/${encodeURIComponent(pid)}`);
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<{
    poId: string;
    poNumber: string;
    items: PoIssueBalanceLine[];
    hasOpenBalance: boolean;
  }>;
}

export async function fetchStoreInwardRegister(q: StoreInwardListQuery = {}): Promise<StoreInwardReceipt[]> {
  const params = new URLSearchParams();
  const lim = Math.min(2000, Math.max(1, q.limit ?? 1500));
  params.set("limit", String(lim));
  if (q.storeYardId?.trim()) params.set("storeYardId", q.storeYardId.trim());
  if (q.search?.trim()) params.set("search", q.search.trim());
  if (q.from?.trim()) params.set("from", q.from.trim());
  if (q.to?.trim()) params.set("to", q.to.trim());
  if (q.sort === "asc" || q.sort === "desc") params.set("sort", q.sort);
  const res = await apiFetch(`${getApiBaseUrl()}/api/inventory/store-inward?${params.toString()}`);
  if (!res.ok) throw new Error(await parseError(res));
  return res.json() as Promise<StoreInwardReceipt[]>;
}
